import { useEffect, useRef } from 'react';

import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';

/**
 * Synchronizacja motywu z kontem usera tenanta (nie renderuje nic) — lustro
 * `AdminAppearanceSync`, ale zapis idzie do `PUT /api/auth/profile`.
 *
 * ODCZYT: po zalogowaniu przyjmuje motyw z konta (serwer wygrywa nad localStorage).
 * ZAPIS: zmiana przez usera → PUT profile (best-effort; localStorage trzyma lokalnie).
 * `settled` chroni przed pętlą (przejściowy render z wartością localStorage nie nadpisze serwera).
 */
export function TenantAppearanceSync() {
  const { me } = useTenantAuth();
  const user = me?.user ?? null;
  const { preset, theme, setPreset, setTheme } = useTheme();

  const hydratedFor = useRef<string | null>(null);
  const serverState = useRef<{ preset: string; mode: string } | null>(null);
  const settled = useRef(false);

  // ODCZYT — po zalogowaniu (raz per user.id).
  useEffect(() => {
    if (!user) {
      hydratedFor.current = null;
      serverState.current = null;
      settled.current = false;
      return;
    }
    if (hydratedFor.current === user.id) return;
    hydratedFor.current = user.id;
    serverState.current = { preset: user.uiThemePreset, mode: user.uiThemeMode };
    settled.current = false;
    setPreset(user.uiThemePreset);
    setTheme(user.uiThemeMode);
  }, [user, setPreset, setTheme]);

  // ZAPIS — zmiana przez usera po ustabilizowaniu się stanu na wartości z konta.
  useEffect(() => {
    if (!user || hydratedFor.current !== user.id || !serverState.current) return;
    const server = serverState.current;
    const matchesServer = preset === server.preset && theme === server.mode;
    if (!settled.current) {
      if (matchesServer) settled.current = true;
      return;
    }
    if (matchesServer) return;
    serverState.current = { preset, mode: theme };
    api.put('/auth/profile', { uiThemePreset: preset, uiThemeMode: theme }).catch(() => {
      // best-effort; wybór i tak zapisany lokalnie w ThemeContext (localStorage)
    });
  }, [user, preset, theme]);

  return null;
}
